import IntegrationInstructionsOutlined from '@mui/icons-material/IntegrationInstructionsOutlined';
import { useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';

import { PartHistoryProps } from './PartHistory.props';
import {
  DateCellWrapper,
  GridContainer,
  SmallPartLabel,
  StyledWrapper,
  XmlButtonWrapper,
} from './PartHistory.styles';
import { ActionPermissions } from '../../../@types';
import { ScreenSize } from '../../../@types/display.types';
import { getUploadedFilePresignedUrlByKey } from '../../../api/uploadedFiles';
import { MOBILE_DATA_GRID_COLUMN } from '../../../configs/ui-constants';
import { formatDate, isSafeUrl } from '../../../utils/common';
import { Accordian } from '../../atoms/Accordian';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { Link } from '../../atoms/Link';
import { NotesCell } from '../../atoms/NotesCell';
import { PermissionLink } from '../../atoms/PermissionLink';
import { Typography, TypographyVariantProps } from '../../atoms/Typography';
import { CustomDataGrid } from '../../layouts/CustomDataGrid';
import { MobileDataGridItem } from '../../organisms/MobileDataGridItem';

export const PartHistory: React.FC<PartHistoryProps> = ({ partId, partHistoryData, isLoading }) => {
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Date/Timestamp',
      renderCell: (params) => (
        <DateCellWrapper>
          <Typography variant={TypographyVariantProps.Body2}>
            {formatDate(params.row.createdAt)}
          </Typography>
          {params.row.attachmentKey && (
            <XmlButtonWrapper>
              <Button
                variant={ButtonVariantProps.Icon}
                aria-label="download"
                onClick={() => downloadPartHistoryFile(params.row.attachmentKey)}
              >
                <IntegrationInstructionsOutlined />
              </Button>
            </XmlButtonWrapper>
          )}
        </DateCellWrapper>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'User',
    },
    {
      field: 'historyTypeCode',
      headerName: 'Code',
    },
    {
      field: 'condition',
      headerName: 'Condition',
    },
    {
      field: 'toNode',
      headerName: 'To',
      valueGetter: (_value, row) => row.toNode,
      renderCell: (params: GridRenderCellParams) =>
        getPartHistoryLocationLinks(
          params.row.toNode,
          params.row.toLocationIds,
          params.row.toLocationNodeTypes
        ),
    },
    {
      field: 'fromNode',
      headerName: 'From',
      valueGetter: (_value, row) => row.fromNode,
      renderCell: (params: GridRenderCellParams) =>
        getPartHistoryLocationLinks(
          params.row.fromNode,
          params.row.fromLocationIds,
          params.row.fromLocationNodeTypes
        ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      maxWidth: 500,
      renderCell: (params: GridRenderCellParams) => <NotesCell value={params.row.notes} />,
    },
    {
      field: 'serviceId',
      headerName: 'Job',
      renderCell: (params: GridRenderCellParams) =>
        params.row.serviceId && (
          <PermissionLink
            permission={ActionPermissions.Service_Edit_View_Service}
            href={`/services/${params.row.serviceId}`}
            sx={{ color: 'v4.accent' }}
          >
            {params.row.serviceId}
          </PermissionLink>
        ),
    },
    {
      field: 'shipmentId',
      headerName: 'Shipment',
      renderCell: (params: GridRenderCellParams) =>
        params.row.shipmentId && (
          <PermissionLink
            permission={ActionPermissions.Shipment_View_Search}
            href={`/shipments/${params.row.shipmentId}`}
            sx={{ color: 'v4.accent' }}
          >
            {params.row.shipmentId}
          </PermissionLink>
        ),
    },
    {
      field: MOBILE_DATA_GRID_COLUMN,
      headerName: '',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <MobileDataGridItem
            data={[
              {
                title: 'Date/Timestamp',
                value: (
                  <Typography variant={TypographyVariantProps.Body2}>
                    {formatDate(row.createdAt)}
                  </Typography>
                ),
              },
              {
                title: 'User',
                value: <SmallPartLabel>{row.createdBy}</SmallPartLabel>,
              },
              {
                title: 'Code',
                value: <SmallPartLabel>{row.historyTypeCode}</SmallPartLabel>,
              },
              {
                title: 'Condition',
                value: <SmallPartLabel>{row.condition}</SmallPartLabel>,
              },
              {
                title: 'To',
                value: <SmallPartLabel>{row.toNode}</SmallPartLabel>,
              },
              {
                title: 'From',
                value: <SmallPartLabel>{row.fromNode}</SmallPartLabel>,
              },
              {
                title: 'Job',
                value: row.serviceId && (
                  <PermissionLink
                    permission={ActionPermissions.Service_Edit_View_Service}
                    href={`/services/${row.serviceId}`}
                    sx={{ color: 'v4.accent' }}
                  >
                    {row.serviceId}
                  </PermissionLink>
                ),
              },
              {
                title: 'Shipment',
                value: row.shipmentId && (
                  <PermissionLink
                    permission={ActionPermissions.Shipment_View_Search}
                    href={`/shipments/${row.shipmentId}`}
                    sx={{ color: 'v4.accent' }}
                  >
                    {row.shipmentId}
                  </PermissionLink>
                ),
              },
            ]}
          />
        );
      },
    },
  ];

  const maxOfCol = (colIndex: number) => {
    const invisibleContainer = document.createElement('div');
    invisibleContainer.setAttribute(
      'style',
      'visibility: hidden; z-index: -9999999999; position: absolute; font-size: 14px; top: 0; left: 0;'
    );
    document.body.append(invisibleContainer);

    const widths: number[] = [];

    document.querySelectorAll(`[aria-colindex="${colIndex + 1}"]`).forEach((cell) => {
      const invisibleCell = document.createElement('div');
      invisibleCell.textContent = cell.textContent;
      invisibleCell.setAttribute('style', 'width: 100%; max-width: 100%; min-width: 100%;');
      invisibleContainer.append(invisibleCell);
      widths.push(Math.ceil(invisibleCell.clientWidth + 1));
    });

    let max = widths.length ? Math.max(...widths) : 0;

    if (max !== 0 && max < 30) {
      max = 30;
    }

    invisibleContainer.remove();

    return max;
  };

  const resizeColumns = () => {
    const cols = [...columns]; // columns is my columns state.

    cols.forEach((col, index) => {
      const maxColWidth = maxOfCol(index) + 20;
      col.width = maxColWidth;
    });

    return cols;
  };

  // ToDo: This is a temporary fix. WIll remove this after Mui-Datagrid-Pro library update
  useEffect(() => {
    if (partHistoryData.length === 0) return;
    const timeoutId = setTimeout(() => {
      setIsPageLoaded(true);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [partHistoryData]);

  const downloadPartHistoryFile = async (key: string) => {
    const fileData = await getUploadedFilePresignedUrlByKey(key);
    if (!fileData?.url) return;
    if (!isSafeUrl(fileData.url)) {
      // Refuse to open anything outside http/https/blob — guards against a
      // compromised presigned-URL endpoint returning e.g. javascript: schemes.
      // Log only the scheme so we don't leak presigned-URL signatures/tokens.
      const scheme = fileData.url.split(':', 1)[0] || 'unknown';
      console.error(`Refusing to open part-history attachment: unsafe URL scheme "${scheme}"`);
      return;
    }
    window.open(fileData.url, '_blank', 'noopener,noreferrer');
  };

  const getPartHistoryLocationLinks = (
    locationData: string,
    locationIdData: string,
    locationNodeTypesData: string
  ) => {
    const locations = locationData?.split(' / ') ?? [];
    const locationIds = locationIdData?.split(' / ') ?? [];
    const locationNodeTypes = locationNodeTypesData?.split(' / ') ?? [];
    return (
      <Grid container>
        {locations.map((location, index) => {
          const id = locationIds[index];
          const nodeType = locationNodeTypes[index];
          const trailingSlash = index !== locations.length - 1 ? ' / ' : '';
          // Without an id we can't build a link target — fall back to plain text
          // rather than emit /structure/undefined (or /configure/users/undefined).
          if (!id) {
            return (
              <Grid key={index}>
                {location}
                {trailingSlash}&nbsp;
              </Grid>
            );
          }
          const isSibling = nodeType === 'sibling';
          return (
            <Grid key={index}>
              <PermissionLink
                permission={
                  isSibling
                    ? ActionPermissions.Configure_User_Edit_General
                    : ActionPermissions.Structure_Edit_View_Structure_Location
                }
                href={isSibling ? `/configure/users/${id}` : `/structure/${id}`}
                sx={{ color: 'v4.accent' }}
              >
                {location}
              </PermissionLink>
              {trailingSlash}&nbsp;
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <StyledWrapper>
      <Accordian
        title={
          <Grid container justifyContent="space-between" width="100%">
            <Grid>
              <Typography>Part History</Typography>
            </Grid>
            <Grid mr={2}>
              <Link href={`/parts/movements?partId=${partId}`} target="_blank">
                <Button variant={ButtonVariantProps.Secondary} onClick={(e) => e.stopPropagation()}>
                  All Records
                </Button>
              </Link>
            </Grid>
          </Grid>
        }
        defaultExpanded={true}
      >
        <GridContainer>
          <CustomDataGrid
            columns={isPageLoaded ? resizeColumns() : columns}
            isMobile={isMobileView}
            rows={partHistoryData}
            getRowId={(row) => row?.id}
            disableColumnFilter={true}
            disableColumnSelector={true}
            disableDensitySelector={true}
            pagination={false}
            enableCSVExport={false}
            exportFileName={''}
            componentsProps={{
              toolbar: {
                csvOptions: { disableToolbarButton: true },
                printOptions: { disableToolbarButton: true },
                showQuickFilter: false,
              },
            }}
            getRowHeight={() => 'auto'}
            loading={isLoading}
            autoHeight
          />
        </GridContainer>
      </Accordian>
    </StyledWrapper>
  );
};
